import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Lock,
  ArrowRight,
  Apple,
  Smartphone,
  Mail,
  Check,
  Sparkles,
  ShieldCheck,
  Clock,
  Share2,
  Copy,
  TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_BASE = 'https://future-vault.com/waitlist';

export default function WaitlistPage() {
  const [searchParams] = useSearchParams();
  const incomingRef = searchParams.get('ref') || '';

  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('ios');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [stats, setStats] = useState(null); // { referral_code, referrals_count, queue_position }
  const [copied, setCopied] = useState(false);

  // Build the personal share URL once a referral code exists.
  const shareUrl = useMemo(() => {
    if (!stats?.referral_code) return '';
    return `${SHARE_BASE}?ref=${stats.referral_code}`;
  }, [stats]);

  // After we render the success screen, periodically refresh the stats so the
  // user sees their counter tick up live when their friends sign up.
  useEffect(() => {
    if (!joined || !stats?.referral_code) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/waitlist/status`, {
          params: { code: stats.referral_code },
        });
        setStats((prev) => ({ ...prev, ...r.data }));
      } catch {
        /* silent — preserves last known stats */
      }
    }, 15000);
    return () => clearInterval(t);
  }, [joined, stats?.referral_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Please enter your email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist/mobile`, {
        email: trimmed,
        platform,
        source: 'web_landing_waitlist',
        ref: incomingRef || undefined,
      });
      setStats({
        referral_code: res.data.referral_code,
        referrals_count: res.data.referrals_count ?? 0,
        queue_position: res.data.queue_position ?? null,
      });
      setJoined(true);
      if (res.data.already_subscribed) {
        toast.success("You're already on the list — here's your referral link.");
      } else {
        toast.success("You're in. Check your inbox for confirmation.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'FutureVault',
          text: "I just joined the FutureVault waitlist. Lock your savings until a future date you pick. Join via my link to bump us both up the line:",
          url: shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="min-h-screen bg-vault" data-testid="waitlist-page">
      {/* Header */}
      <header className="glass-header fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="waitlist-header-logo">
              <div className="h-10 w-10 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-white tracking-tight">
                FutureVault
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/" className="hidden sm:block">
                <Button variant="ghost" data-testid="waitlist-nav-home" className="text-[#A19DB0] hover:text-white hover:bg-white/5">
                  Use Web App
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#9333EA]/10 border border-[#9333EA]/30 mb-8" data-testid="waitlist-badge">
            <Sparkles className="h-3.5 w-3.5 text-[#9333EA]" />
            <span className="text-xs font-medium text-[#C8A6FF] tracking-wide uppercase">Mobile App Launching Soon</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-none mb-6" data-testid="waitlist-hero-heading">
            Lock your savings.
            <br />
            <span className="text-[#9333EA]">From your pocket.</span>
          </h1>

          <p className="text-lg text-[#A19DB0] leading-relaxed mb-6 max-w-xl mx-auto" data-testid="waitlist-hero-subtitle">
            FutureVault for iOS is in final review with Apple. Be first in line —
            we'll email you the moment it hits the App Store.
          </p>

          {/* Referrer pill: only if user landed via someone's link */}
          {incomingRef && !joined && (
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs text-[#A19DB0] mb-6"
              data-testid="waitlist-referrer-pill"
            >
              <Sparkles className="h-3 w-3 text-[#9333EA]" />
              You were invited by a friend. Their queue position climbs when you join.
            </div>
          )}

          {/* Sign-up form / Success state */}
          {!joined ? (
            <form onSubmit={handleSubmit} className="max-w-lg mx-auto" data-testid="waitlist-form">
              <div className="flex flex-col gap-3 mb-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A19DB0]" />
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    data-testid="waitlist-email-input"
                    className="h-14 pl-12 pr-4 text-base bg-white/5 border-white/10 text-white placeholder:text-[#6F6981] focus:border-[#9333EA] focus:ring-[#9333EA]"
                  />
                </div>

                {/* Platform toggle */}
                <div className="grid grid-cols-2 gap-2" data-testid="waitlist-platform-toggle">
                  <button
                    type="button"
                    onClick={() => setPlatform('ios')}
                    data-testid="waitlist-platform-ios"
                    className={`h-12 rounded-md border flex items-center justify-center gap-2 transition-all ${
                      platform === 'ios'
                        ? 'bg-[#9333EA]/15 border-[#9333EA] text-white'
                        : 'bg-white/5 border-white/10 text-[#A19DB0] hover:border-white/20'
                    }`}
                  >
                    <Apple className="h-4 w-4" />
                    <span className="text-sm font-medium">iOS</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlatform('android')}
                    data-testid="waitlist-platform-android"
                    className={`h-12 rounded-md border flex items-center justify-center gap-2 transition-all ${
                      platform === 'android'
                        ? 'bg-[#9333EA]/15 border-[#9333EA] text-white'
                        : 'bg-white/5 border-white/10 text-[#A19DB0] hover:border-white/20'
                    }`}
                  >
                    <Smartphone className="h-4 w-4" />
                    <span className="text-sm font-medium">Android</span>
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                data-testid="waitlist-submit-button"
                className="btn-primary h-14 w-full text-lg"
              >
                {submitting ? 'Adding you...' : (
                  <>
                    Join the Waitlist
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="mt-4 text-xs text-[#6F6981]" data-testid="waitlist-privacy-note">
                No spam. Launch email only. Unsubscribe with one click.
              </p>
            </form>
          ) : (
            <div className="max-w-lg mx-auto" data-testid="waitlist-success">
              <div className="rounded-2xl bg-[#9333EA]/10 border border-[#9333EA]/30 p-6 sm:p-8 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-12 w-12 rounded-full bg-[#9333EA] flex items-center justify-center shrink-0">
                    <Check className="h-6 w-6 text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-white" data-testid="waitlist-success-heading">
                      You're on the list.
                    </h2>
                    <p className="text-sm text-[#A19DB0]" data-testid="waitlist-success-email">{email}</p>
                  </div>
                </div>

                {/* Position + referrals stats */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="rounded-lg bg-black/30 border border-white/5 p-4" data-testid="waitlist-stat-position">
                    <div className="text-[10px] uppercase tracking-wider text-[#6F6981] mb-1">Your position</div>
                    <div className="font-heading text-3xl font-black text-white tabular-nums">
                      {stats?.queue_position != null ? `#${stats.queue_position}` : '—'}
                    </div>
                  </div>
                  <div className="rounded-lg bg-black/30 border border-white/5 p-4" data-testid="waitlist-stat-referrals">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6F6981] mb-1">
                      <TrendingUp className="h-3 w-3" /> Referrals
                    </div>
                    <div className="font-heading text-3xl font-black text-white tabular-nums">
                      {stats?.referrals_count ?? 0}
                    </div>
                  </div>
                </div>

                {/* Referral link block */}
                <div className="rounded-lg bg-[#0A0A0F] border border-white/10 p-4 mb-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-[#C8A6FF] mb-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    Skip the line — share your link
                  </div>
                  <p className="text-xs text-[#A19DB0] mb-3 leading-relaxed">
                    Every friend who joins via your personal link bumps you up the queue.
                  </p>
                  <div className="flex items-stretch gap-2 mb-3">
                    <code
                      className="flex-1 truncate rounded-md bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white font-mono"
                      data-testid="waitlist-referral-url"
                    >
                      {shareUrl || '—'}
                    </code>
                    <Button
                      type="button"
                      onClick={handleCopy}
                      disabled={!shareUrl}
                      data-testid="waitlist-copy-button"
                      variant="outline"
                      className="shrink-0 h-auto px-3 border-white/15 text-white hover:bg-white/5 hover:text-white"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button
                    type="button"
                    onClick={handleNativeShare}
                    disabled={!shareUrl}
                    data-testid="waitlist-share-button"
                    className="btn-primary w-full h-11"
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    Share your link
                  </Button>
                </div>

                <Link to="/">
                  <Button variant="ghost" data-testid="waitlist-success-cta" className="w-full text-[#A19DB0] hover:text-white hover:bg-white/5">
                    Start Saving on Web Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why-wait strip */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-6" data-testid="waitlist-perk-1">
              <ShieldCheck className="h-7 w-7 text-[#9333EA] mb-3" />
              <h3 className="font-heading text-lg font-semibold text-white mb-1">Real Lockbox</h3>
              <p className="text-sm text-[#A19DB0]">Your money sits in your own Stripe account — locked until your date.</p>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-6" data-testid="waitlist-perk-2">
              <Clock className="h-7 w-7 text-[#9333EA] mb-3" />
              <h3 className="font-heading text-lg font-semibold text-white mb-1">No Willpower</h3>
              <p className="text-sm text-[#A19DB0]">Pick a future date. We hold the line so you can't break it on a bad day.</p>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-6" data-testid="waitlist-perk-3">
              <Sparkles className="h-7 w-7 text-[#9333EA] mb-3" />
              <h3 className="font-heading text-lg font-semibold text-white mb-1">Launch Perks</h3>
              <p className="text-sm text-[#A19DB0]">Waitlist members get early access and an extended free Premium trial.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#6F6981]" data-testid="waitlist-footer-copy">
            © {new Date().getFullYear()} FutureVault. Lock your savings. Unlock your future.
          </p>
          <div className="flex items-center gap-5 text-xs text-[#6F6981]">
            <Link to="/refunds" className="hover:text-white transition-colors" data-testid="waitlist-footer-refunds">Refunds</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="waitlist-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="waitlist-footer-terms">Terms</Link>
            <Link to="/support" className="hover:text-white transition-colors" data-testid="waitlist-footer-support">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
